import { create } from 'zustand';

/**
 * The celebration overlay's gate — state only, no rendering, no route knowledge
 * (`docs/DESIGN.md` §21). The owner's rule is "interrupt for the rare, decorate for the
 * common": exactly level-up, first-time badge and collection-completed get a full-screen
 * overlay; everything else stays a toast. This store enforces the three rules that follow
 * from that choice — nothing plays over a full-screen route, one at a time, and a capped
 * release on return — without knowing what a route or a pathname is. Task 9's component maps
 * a pathname onto `setBlocked`; keeping the gate a plain boolean here is deliberate, so a
 * future change can't grow routing rules inside the queue itself.
 */

export type CelebrationKind = 'level' | 'badge' | 'collection';
export type Celebration = { kind: CelebrationKind; title: string; sub: string; level?: number };

/** A player returning after a long absence gets three celebrations and the rest as nothing —
 *  never nine modals in a row. */
export const MAX_PER_RETURN = 3;

type State = {
  /** Held celebrations, oldest first. Populated by `request()` while something is already
   *  showing or the queue is blocked, and by `setBlocked(true)` folding back whatever was
   *  `current` at the moment a full-screen route took over. */
  pending: Celebration[];
  /** The one celebration Task 9 is allowed to render. Always `null` while `blocked` is
   *  true — that equivalence IS rule 1 ("nothing may play while a full-screen route owns the
   *  screen"), not just a side effect of how requests happen to be routed. */
  current: Celebration | null;
  /** True while a full-screen route owns the screen (battle, a minigame, the wardrobe).
   *  Deliberately a plain boolean and not a pathname: route knowledge lives in the component
   *  that mounts the overlay, so nothing here can grow its own routing rules. */
  blocked: boolean;
  /** Queue a celebration, or promote it straight to `current` if the coast is clear. */
  request: (c: Celebration) => void;
  /** Enter or leave the blocked state. Entering folds a mid-play `current` back to the front
   *  of `pending` rather than dropping it (rule 1); leaving releases up to `MAX_PER_RETURN`
   *  total — counting anything folded back in, not on top of it — and discards the rest (rule
   *  3). Both directions are idempotent: calling with the value already in effect is a no-op,
   *  so double-invocation under `<StrictMode>` can't duplicate or drop a held celebration. */
  setBlocked: (b: boolean) => void;
  /** Advance to the next pending celebration, or to nothing. Safe with nothing current. */
  dismiss: () => void;
  reset: () => void;
};

export const useCelebrationQueue = create<State>((set, get) => ({
  pending: [],
  current: null,
  blocked: false,
  request: (c) => {
    const { blocked, current, pending } = get();
    if (blocked || current) { set({ pending: [...pending, c] }); return; }
    set({ current: c });
  },
  setBlocked: (b) => {
    const { blocked: wasBlocked, current, pending } = get();
    if (b) {
      if (wasBlocked) return; // already blocked — no-op, nothing to fold twice
      // Nothing may play while blocked: whatever was showing goes back to the front of the
      // queue, ahead of anything already held, so it resumes first on return.
      set({ blocked: true, current: null, pending: current ? [current, ...pending] : pending });
      return;
    }
    if (!wasBlocked) return; // nothing was held — no-op
    // `current` is always null here (the `blocked` invariant above), so the cap applies to
    // `pending` alone: release up to MAX_PER_RETURN in total, oldest first, discard the rest.
    const allowed = pending.slice(0, MAX_PER_RETURN);
    set({ blocked: false, current: allowed[0] ?? null, pending: allowed.slice(1) });
  },
  dismiss: () => {
    const [next, ...rest] = get().pending;
    set({ current: next ?? null, pending: rest });
  },
  reset: () => set({ pending: [], current: null, blocked: false }),
}));
